import { useMutation, useApolloClient } from '@apollo/client';
import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';

import { gql } from '../../../generated';
import { AuthLogin2FaMutationVariables } from '../../../generated/graphql';
import { urls } from '../../constants/v2/urls';
import { usePathname, useRouter } from '../../i18n/routing';
import { getAccessToken, parseToken, removeAccessToken, setAccessToken } from '../../utils/auth';

const ERROR_TOKEN = 'OTP_TOKEN';

const AUTH_LOGIN = gql(/* GraphQL */ `
  mutation AuthLogin($username: String!, $password: String!) {
    authLogin(username: $username, password: $password) {
      access_token
      error_code
    }
  }
`);

const AUTH_LOGIN_2FA = gql(/* GraphQL */ `
  mutation AuthLogin2FA($username: String!, $password: String!, $token: String) {
    authLogin(username: $username, password: $password, token: $token) {
      access_token
      error_code
    }
  }
`);

const USER_ACTIVATE = gql(/* GraphQL */ `
  mutation usersActivate($token: String!) {
    usersActivate(token: $token) {
      access_token
    }
  }
`);

export const useAuth = (is2Fa: boolean = false) => {
  const apolloClient = useApolloClient();
  const router = useRouter();
  const pathname = usePathname();

  const [login] = useMutation(is2Fa ? AUTH_LOGIN_2FA : AUTH_LOGIN, {
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
    context: {
      noAuth: true,
    },
  });

  const [activate] = useMutation(USER_ACTIVATE, {
    onError: (_) => {},
    onCompleted: ({ usersActivate }) => {
      if (usersActivate) {
        setAccessToken(usersActivate.access_token);
      }
    },
    context: {
      noAuth: true,
    },
  });

  const logout = useCallback(() => {
    removeAccessToken();

    router.push({
      pathname: urls.Login,
      query: { returnUrl: pathname },
    });

    location.reload();
  }, [pathname, router]);

  const check_access = useCallback(() => {
    const accessToken = getAccessToken();

    if (!accessToken) {
      return false;
    }

    return parseToken(accessToken) ?? false;
  }, []);

  return {
    login: useCallback(
      async (variables: AuthLogin2FaMutationVariables) => {
        const response = await login({
          variables,
        });

        if (response.errors) {
          return {
            ...response,
            customErrors: null,
          };
        }

        await apolloClient.cache.reset();

        const { access_token, error_code } = response.data?.authLogin ?? {};

        if (error_code === ERROR_TOKEN) {
          return {
            ...response,
            customErrors: ['2fa'],
          };
        }

        const userInfo = parseToken(access_token ?? '');

        if (!userInfo) {
          enqueueSnackbar('Token error', {
            variant: 'error',
          });

          return {
            ...response,
            customErrors: ['tokenError'],
          };
        }

        if (userInfo.hasura['x-hasura-default-role'] !== 'partner') {
          enqueueSnackbar("Can't access", {
            variant: 'error',
          });

          return {
            ...response,
            customErrors: ["Can't access"],
          };
        }

        setAccessToken(access_token ?? '');

        enqueueSnackbar('Success', {
          variant: 'success',
        });

        return {
          ...response,
          customErrors: null,
        };
      },
      [apolloClient.cache, login]
    ),
    activate,
    logout,
    check_access,
  };
};
